<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="busy" kind="loading"></report-state>
    <report-state
      v-else-if="!rows.length"
      icon="mdi-package-variant-closed"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <div v-else class="rd-card rd-card--scroll">
      <table class="rd-tbl">
        <thead>
          <tr>
            <th scope="col" class="rd-col-num">#</th>
            <th scope="col">{{ $t("employee") }}</th>
            <th scope="col">{{ $t("restaurant") }}</th>
            <th scope="col">{{ $t("meals") }}</th>
            <th v-if="hasLocation" scope="col">{{ $t("delivery_location") }}</th>
            <th scope="col" class="rd-col-go"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in rows"
            :key="row.key"
            data-open
            tabindex="0"
            :class="{ 'is-selected': isOpen(row) }"
            @click="openOrder(row, $event)"
            @keydown.enter.prevent="openOrder(row, $event)"
            @keydown.space.prevent="openOrder(row, $event)"
          >
            <td class="rd-num rd-muted">{{ index + 1 }}</td>
            <td class="is-name" :title="row.worker">
              <span class="rd-person">
                <span class="rd-avatar rd-avatar--sm">{{ row.initials }}</span>
                <span class="rd-person__name">{{ row.worker }}</span>
              </span>
            </td>
            <td class="rd-muted">{{ row.restaurant }}</td>
            <td class="mp-meals">
              <span v-for="(meal, mealIndex) in row.meals" :key="mealIndex" class="mp-meals__item">
                <b class="rd-num">{{ meal.quantity }}×</b> {{ meal.name }}
                <report-shift-tag v-if="meal.shiftNumber" :shift="meal.shiftNumber"></report-shift-tag>
              </span>
            </td>
            <td v-if="hasLocation" class="rd-muted">{{ row.location }}</td>
            <td class="rd-col-go"><v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import CompanyMultiRestaurantTabPackages from "@/components/company/reports/multi-restaurant-tabs/CompanyMultiRestaurantTabPackages.vue";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import hasMultiFeed from "../../../hasMultiFeed";
import CompanyDesktopMultiOrder from "./CompanyDesktopMultiOrder.vue";

const initialsOf = (name) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join("");

/**
 * Pakovanje firme "više restorana" na računaru: narudžbe radnika po restoranu (šta je naručeno i u kojoj smeni).
 * Klik na red otvara detalje narudžbe (sa cenama) u panelu. Restorane i period čita iz zajedničke trake, pretragu
 * (radnik, restoran ili obrok) takođe. Nasleđuje učitavanje, štampu i Excel iz starog taba.
 */
export default {
  name: "CompanyDesktopMultiPackages",
  extends: CompanyMultiRestaurantTabPackages,
  mixins: [hasMultiFeed],
  components: { ReportShiftTag, ReportState, ReportTools },
  inject: {
    shell: { from: "reportShell", default: null },
  },
  computed: {
    busy() {
      return this.loading;
    },
    hasRows() {
      return this.rows.length > 0;
    },
    /** Narudžbe posle pretrage; redovi su samo za prikaz (štampa i Excel čitaju `orders`). */
    rows() {
      const search = this.toLatin(String(this.$store.state.report.filters.search || "")).toLowerCase().trim();
      return this.orders
        .map((order) => ({
          key: String(order.id),
          order,
          worker: this.toLatin(order.user_name || ""),
          initials: initialsOf(this.toLatin(order.user_name || "")),
          restaurant: order.restaurant,
          location: this.toLatin(order.delivery_location || ""),
          meals: order.meals.map((meal) => ({
            quantity: meal.quantity,
            name: this.toLatin(meal.name || ""),
            shiftNumber: Number(this.formatShiftNumber(meal.shift)) || null,
          })),
        }))
        .filter(
          (row) =>
            !search ||
            [row.worker, row.restaurant, row.location, ...row.meals.map((meal) => meal.name)]
              .join(" ")
              .toLowerCase()
              .includes(search)
        );
    },
    /** Mesto isporuke se prikazuje samo kad ga ijedna narudžba ima. */
    hasLocation() {
      return this.rows.some((row) => row.location);
    },
    emptyTitle() {
      const search = this.$store.state.report.filters.search;
      return search ? this.$t("report_mobile.no_results_for", { search }) : this.$t("no_orders");
    },
    emptyText() {
      return this.$store.state.report.filters.search ? "" : this.$t("no_orders_for_selected_date");
    },
    kpis() {
      const meals = this.rows.reduce((sum, row) => sum + row.meals.reduce((inner, meal) => inner + meal.quantity, 0), 0);
      return [
        { key: "orders", label: this.$t("orders"), value: this.rows.length, tone: "inverse" },
        { key: "meals", label: this.$t("report_desktop.total_meals"), value: meals },
        { key: "workers", label: this.$t("report_desktop.workers"), value: new Set(this.rows.map((row) => row.worker)).size },
      ];
    },
  },
  methods: {
    isOpen(row) {
      const top = this.shell?.panelStack?.[0];
      return Boolean(top && top.key === `order-${row.key}`);
    },
    /** Detalji narudžbe u panelu: radnik, restoran, obroci sa smenom i cenom. */
    openOrder(row, event) {
      this.$emit("open-panel", {
        opener: event?.currentTarget,
        key: `order-${row.key}`,
        root: true,
        title: row.worker,
        subtitle: [`#${row.order.id}`, row.restaurant].filter(Boolean).join(" · "),
        component: CompanyDesktopMultiOrder,
        props: { order: row.order },
      });
    },
    async copyTable() {
      const headers = [this.$t("employee"), this.$t("restaurant"), this.$t("meals"), ...(this.hasLocation ? [this.$t("delivery_location")] : [])];
      const rows = this.rows.map((row) => [
        row.worker,
        row.restaurant,
        row.meals.map((meal) => `${meal.quantity}x ${meal.name}`).join(", "),
        ...(this.hasLocation ? [row.location] : []),
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
